import { useTheme } from '../../../theme';
import type { CSSObject } from '@emotion/react';
import { CalloutProps } from './callout';

type Return = Record<
  'container' | 'textWrapper' | 'text' | 'title',
  CSSObject
> & {
  iconColor: string;
};

type CalloutLevelAsRecord = Partial<
  Record<NonNullable<CalloutProps['level']>, string>
>;

type Props = {
  level: CalloutProps['level'];
  size: CalloutProps['size'];
};

export const useCalloutStyles = ({
  level = 'neutral',
  size = 'default',
}: Props): Return => {
  const theme = useTheme();

  const levelToContainerColorMap: CalloutLevelAsRecord = {
    success: theme.callout.success,
    informative: theme.callout.informative,
    notice: theme.callout.notice,
    error: theme.callout.error,
    neutral: theme.callout.neutral,
  };

  const levelToIconColorMap: CalloutLevelAsRecord = {
    neutral: theme.icon.primary,
  };

  return {
    container: {
      display: 'flex',
      width: '100%',
      padding: size === 'small' ? theme.spacing(2) : theme.spacing(3),
      alignItems: 'flex-start',
      gap: size === 'small' ? theme.spacing(2) : theme.spacing(3),
      borderRadius: theme.shape.radius8,
      color: theme.text.generic.secondary,
      boxSizing: 'border-box',
      backgroundColor: levelToContainerColorMap[level],
    },
    textWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
      flex: '1 0 0',
    },
    text: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      flex: '1 0 0',
      alignSelf: 'stretch',
    },
    iconColor: levelToIconColorMap[level] || theme.icon.primary,
    title: {
      color: theme.text.generic.secondary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontVariantNumeric: 'lining-nums tabular-nums',
      display: 'flex',
      minHeight: 20,
      flexDirection: 'column',
      justifyContent: 'center',
      alignSelf: 'stretch',
    },
  };
};
